import { useCallback, useRef } from 'react'
import { type Generation } from '../lib/api'
import { getModelById } from '../lib/models'
import { downloadGeneration } from '../lib/share'
import VideoPlayer from './VideoPlayer'

interface GenerationCardProps {
  generation: Generation
  onClick: (generation: Generation) => void
  onDelete: (id: string) => void
  onReuse?: (generation: Generation) => void
}

export default function GenerationCard({
  generation,
  onClick,
  onDelete,
  onReuse,
}: GenerationCardProps) {
  const model = getModelById(generation.model)
  const cardRef = useRef<HTMLDivElement>(null)

  const handleClick = useCallback(() => {
    if (generation.status === 'complete') onClick(generation)
  }, [generation, onClick])

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onDelete(generation.id)
    },
    [generation.id, onDelete],
  )

  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      void downloadGeneration(generation).catch((err) => {
        console.error('Download failed:', err)
      })
    },
    [generation],
  )

  const handleReuse = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onReuse?.(generation)
    },
    [generation, onReuse],
  )

  const isPending = generation.status === 'pending' || generation.status === 'generating'
  const isFailed = generation.status === 'failed'

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className={`group relative aspect-square rounded-xl overflow-hidden border border-border bg-bg-card transition-all hover:border-border-hover ${
        generation.status === 'complete' ? 'cursor-pointer' : ''
      }`}
    >
      {generation.status === 'complete' && generation.r2_url && (
        <>
          {generation.type === 'video' ? (
            <VideoPlayer
              src={generation.r2_url}
              poster={generation.thumbnail_r2_key}
              autoPlay
              controls={false}
            />
          ) : (
            <img
              src={generation.r2_url}
              alt={generation.prompt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </>
      )}

      {isPending && (
        <>
          <div className="absolute inset-0 bg-bg-elevated/40" />
          <div className="scan-beam" />
          <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neon/80">
              Developing
            </span>
          </div>
        </>
      )}

      {isFailed && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center space-y-2">
            <div className="w-8 h-8 mx-auto rounded-full bg-error/20 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-error"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <p className="text-xs text-error font-semibold uppercase tracking-wider">
              Failed
            </p>
            <p className="text-[10px] text-text-muted px-3 line-clamp-2">
              {generation.prompt}
            </p>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1 px-3 py-1 text-[10px] font-mono uppercase tracking-wider rounded-md border border-border hover:border-error hover:text-error text-text-muted transition-colors cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Remove
            </button>
          </div>
        </div>
      )}

      {generation.type === 'video' && generation.status === 'complete' && (
        <div className="absolute top-2 left-2 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="white"
            className="drop-shadow-lg opacity-80 group-hover:opacity-0 transition-opacity"
          >
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </div>
      )}

      {generation.nsfw && (
        <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-bold bg-error/90 text-white rounded z-10">
          NSFW
        </span>
      )}

      {generation.status === 'complete' && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
          <p className="text-xs text-white line-clamp-2 mb-1">{generation.prompt}</p>
          <p className="text-[10px] text-text-secondary mb-2">
            {model?.name ?? generation.model}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
              title="Download"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            {onReuse && (
              <button
                onClick={handleReuse}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-neon/20 transition-colors cursor-pointer"
                title="Use this prompt"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-error/30 transition-colors cursor-pointer ml-auto"
              title="Delete"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
